import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TabsContent } from '@/components/ui/tabs'
import { postApiActivitiesWallBackgroundMutation } from '@/lib/api/client/@tanstack/react-query.gen'
import { type WallState } from '@/lib/firebase/activities'
import { cn } from '@/lib/utils'

interface Props {
  localSettings: WallState['settings']
  setLocalSettings: React.Dispatch<React.SetStateAction<WallState['settings']>>
  onSave: () => void
}

export function WallSettingsTab({
  localSettings,
  setLocalSettings,
  onSave,
}: Props) {
  const uploadMutation = useMutation(postApiActivitiesWallBackgroundMutation())

  const handleBackgroundUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const toastId = toast.loading('กำลังอัปโหลดรูปภาพพื้นหลัง...')

    uploadMutation.mutate(
      { body: { file } },
      {
        onSuccess: (data) => {
          if (data?.success && data?.url && data?.path) {
            setLocalSettings((prev) => ({
              ...prev,
              backgroundImagePath: data.path,
              backgroundImageUrl: data.url,
            }))
            toast.success('อัปโหลดรูปภาพพื้นหลังสำเร็จ', { id: toastId })
          } else {
            toast.error('ไม่สามารถอัปโหลดรูปภาพได้', { id: toastId })
          }
        },
        onError: (error) => {
          console.error('Error uploading background image:', error)
          toast.error('ไม่สามารถอัปโหลดรูปภาพได้ กรุณาลองใหม่อีกครั้ง', {
            id: toastId,
          })
        },
      },
    )

    e.target.value = ''
  }

  return (
    <TabsContent value="settings">
      <Card className="px-4 py-6">
        <CardHeader>
          <CardTitle className="font-bold">การตั้งค่า</CardTitle>
          <CardDescription>
            ปรับแต่งรูปแบบการแสดงผลและพฤติกรรมการสุ่ม
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Background image */}
          <div className="space-y-3">
            <Label className="text-sm font-bold tracking-wider uppercase">
              ภาพพื้นหลังหน้าจอแสดงผล (Background Image)
            </Label>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="bg-muted/40 border-muted-foreground/10 relative flex h-32 w-56 shrink-0 items-center justify-center overflow-hidden rounded-2xl border">
                {localSettings.backgroundImageUrl ? (
                  <>
                    <img
                      src={localSettings.backgroundImageUrl}
                      alt="Background Preview"
                      className="size-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setLocalSettings((prev) => ({
                          ...prev,
                          backgroundImagePath: '',
                          backgroundImageUrl: '',
                        }))
                      }
                      className="absolute top-2 right-2 flex size-6 items-center justify-center rounded-full bg-black/60 text-xs text-white transition-all hover:bg-black/80"
                      title="Remove background"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <div className="text-muted-foreground flex flex-col items-center gap-1 text-center text-xs">
                    <span className="font-bold">ใช้ภาพพื้นหลังเริ่มต้น</span>
                    <span className="text-[10px] opacity-75">
                      (Gradient โทนชมพูหวาน)
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="relative inline-block">
                  <input
                    type="file"
                    accept="image/*"
                    aria-label="อัปโหลดภาพพื้นหลัง"
                    onChange={handleBackgroundUpload}
                    disabled={uploadMutation.isPending}
                    className="absolute inset-0 w-40 cursor-pointer opacity-0"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploadMutation.isPending}
                    className="border-primary/20 text-primary hover:bg-primary/5 h-10 w-40 rounded-xl font-bold"
                  >
                    {uploadMutation.isPending
                      ? 'กำลังอัปโหลด...'
                      : 'อัปโหลดรูปภาพ'}
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs">
                  ภาพแนวนอน (16:9) เช่น JPG, PNG หรือ WebP ขนาดไม่เกิน 5MB
                </p>
              </div>
            </div>
          </div>

          {/* Display mode */}
          <div className="space-y-3">
            <Label className="text-sm font-bold tracking-wider uppercase">
              รูปแบบการแสดงผล
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {(['bubbles', 'wheel', 'slots'] as const).map((mode) => (
                <Button
                  key={mode}
                  variant={
                    localSettings.displayMethod === mode ? 'default' : 'outline'
                  }
                  onClick={() =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      displayMethod: mode,
                    }))
                  }
                  className={cn(
                    'rounded-xl',
                    localSettings.displayMethod === mode
                      ? 'bg-primary text-white shadow-md'
                      : 'border-primary/20 text-primary/70 hover:bg-primary/5',
                  )}
                >
                  {mode === 'bubbles'
                    ? 'Bubbles'
                    : mode === 'wheel'
                      ? 'Wheel'
                      : 'Slots'}
                </Button>
              ))}
            </div>
          </div>

          {/* Marital status filter */}
          <div className="space-y-3">
            <Label className="text-sm font-bold tracking-wider uppercase">
              กรองตามสถานะ
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {(
                [
                  { value: [], label: 'ทั้งหมด' },
                  { value: ['single'], label: 'โสด' },
                  { value: ['not_single'], label: 'ไม่โสด' },
                  { value: ['prefer_not_to_say'], label: 'ไม่ระบุ' },
                ] as const
              ).map((opt) => {
                const current = localSettings.maritalStatusFilter ?? []
                const isActive =
                  opt.value.length === 0
                    ? current.length === 0
                    : current.length === opt.value.length &&
                      opt.value.every((v) => current.includes(v))
                return (
                  <Button
                    key={opt.label}
                    variant={isActive ? 'default' : 'outline'}
                    onClick={() =>
                      setLocalSettings((prev) => ({
                        ...prev,
                        maritalStatusFilter: [...opt.value],
                      }))
                    }
                    className={cn(
                      'rounded-xl',
                      isActive
                        ? 'bg-primary text-white shadow-md'
                        : 'border-primary/20 text-primary/70 hover:bg-primary/5',
                    )}
                  >
                    {opt.label}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* Spin duration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                ระยะเวลาสุ่ม (วินาที)
              </Label>
              <span className="text-primary text-sm font-bold">
                {localSettings.spinDuration}s
              </span>
            </div>
            <Input
              type="range"
              aria-label="ระยะเวลาสุ่ม (วินาที)"
              min="2"
              max="15"
              step="1"
              value={localSettings.spinDuration}
              onChange={(e) =>
                setLocalSettings((prev) => ({
                  ...prev,
                  spinDuration: Number(e.target.value) || 5,
                }))
              }
              className="h-2 w-full appearance-none rounded-full accent-rose-500 outline-none"
            />
          </div>

          {/* Exclude winners */}
          <div className="bg-muted/40 flex items-center justify-between rounded-2xl p-4">
            <div>
              <Label className="text-sm font-bold">ไม่สุ่มซ้ำผู้ชนะเดิม</Label>
              <p className="text-muted-foreground text-xs">
                ข้ามแขกที่ชนะไปแล้วในรอบนี้
              </p>
            </div>
            <input
              type="checkbox"
              aria-label="ไม่สุ่มซ้ำผู้ชนะเดิม"
              checked={localSettings.excludeWinners}
              onChange={(e) =>
                setLocalSettings((prev) => ({
                  ...prev,
                  excludeWinners: e.target.checked,
                }))
              }
              className="size-6 rounded-md accent-rose-500"
            />
          </div>

          {/* Auto-close */}
          <div className="space-y-4">
            <div className="bg-muted/40 flex items-center justify-between rounded-2xl p-4">
              <div>
                <Label className="text-sm font-bold">
                  ปิดหน้าต่างผู้ชนะอัตโนมัติ
                </Label>
              </div>
              <input
                type="checkbox"
                aria-label="ปิดหน้าต่างผู้ชนะอัตโนมัติ"
                checked={localSettings.autoClose}
                onChange={(e) =>
                  setLocalSettings((prev) => ({
                    ...prev,
                    autoClose: e.target.checked,
                  }))
                }
                className="size-6 rounded-md accent-rose-500"
              />
            </div>
            {localSettings.autoClose && (
              <div className="space-y-2 pl-4">
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground text-xs">
                    ปิดหลัง {localSettings.autoCloseDelay}s
                  </Label>
                </div>
                <Input
                  type="range"
                  aria-label="ระยะเวลาก่อนปิดอัตโนมัติ (วินาที)"
                  min="3"
                  max="30"
                  step="1"
                  value={localSettings.autoCloseDelay}
                  onChange={(e) =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      autoCloseDelay: Number(e.target.value) || 10,
                    }))
                  }
                  className="h-1 w-full appearance-none rounded-full accent-rose-500 outline-none"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <Button
              className="bg-primary hover:bg-primary/90 shadow-primary/20 h-12 rounded-xl px-10 font-bold shadow-lg transition-all active:scale-95"
              onClick={onSave}
            >
              บันทึกการตั้งค่า
            </Button>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
